import {ChangeDetectionStrategy, Component, inject, Input, OnInit} from '@angular/core';
import {BaseComponent, TemplateFeature} from 'compob2b-libs';
import {FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {
  TemplateFeatureService,
  TemplateFeatureValue
} from '../../../../../../compob2b-admin/src/app/cabinet/pim/product/create-update/features/template-feature.service'

import {NbTooltipModule} from '@nebular/theme';
import {RouterLink} from '@angular/router';
import {TuiDay} from '@taiga-ui/cdk';
import { TuiCalendar, TuiIcon, TuiInput } from '@taiga-ui/core';
import {TuiInputDate} from '@taiga-ui/kit';

@Component({
  selector: 'app-tf-date',
  templateUrl: './tf-date.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NbTooltipModule, FormsModule, ReactiveFormsModule, RouterLink, TuiIcon, TuiInputDate, TuiInput, TuiCalendar]
})
export class TfDateComponent extends BaseComponent implements OnInit {
  @Input() feature: TemplateFeature;
  @Input() featureLink = false;

  form = new UntypedFormGroup({
    featureValue: new UntypedFormControl(null)
  });

  templateFeatureService = inject(TemplateFeatureService);

  ngOnInit(): void {
    this.featureValue.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe),
      )
      .subscribe((day: TuiDay | null) => {
        this.setFeature(day ? this.tuiDayToIso(day) : null, this.isValid(), this.isShowEmptyWarning());
        this.cdr.detectChanges();
      });

    this.setDefaultValue();

    this.templateFeatureService.isFeaturesModelReloaded()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(_ => {
        this.setDefaultValue();
        this.cdr.detectChanges();
      });
  }

  private setDefaultValue(): void {
    const featureModel = this.templateFeatureService.getFeatureModel();

    if (this.feature.id in featureModel) {
      const rawValue = featureModel[this.feature.id];
      if (rawValue === null || rawValue.toString().trim().length === 0) {
        this.featureValue.setValue(null, {emitEvent: false});
        this.setDefaultFeatureValue(null, this.isValid(), this.isShowEmptyWarning());
      } else {
        this.featureValue.setValue(this.isoToTuiDay(rawValue.toString()), {emitEvent: false});
        this.setDefaultFeatureValue(rawValue.toString(), this.isValid(), this.isShowEmptyWarning());
      }
    } else {
      this.featureValue.setValue(null, {emitEvent: false});
      this.setDefaultFeatureValue(null, this.isValid(), this.isShowEmptyWarning());
    }
  }

  private setDefaultFeatureValue(featureValue: TemplateFeatureValue, valid: boolean, empty: boolean): void {
    this.templateFeatureService.setDefaultFeatureValue(
      this.feature.groupId,
      this.feature.id,
      featureValue,
      valid,
      empty
    );
  }

  private setFeature(featureValue: TemplateFeatureValue, valid: boolean, empty: boolean): void {
    this.templateFeatureService.setFeature(
      this.feature.groupId,
      this.feature.id,
      featureValue,
      valid,
      empty
    );
  }

  isEmpty(): boolean {
    return !this.featureValue.value;
  }

  isRequired(): boolean {
    return this.feature.required;
  }

  isShowEmptyWarning(): boolean {
    return this.isRequired() && this.isEmpty();
  }

  isValid(): boolean {
    return this.form.valid;
  }

  get featureValue(): any {
    return this.form.get('featureValue');
  }

  private isoToTuiDay(iso: string): TuiDay {
    const parts = iso.split('-');
    return new TuiDay(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  }

  private tuiDayToIso(day: TuiDay): string {
    return `${day.year}-${String(day.month + 1).padStart(2, '0')}-${String(day.day).padStart(2, '0')}`;
  }
}
